import { PointCloudStylingCapabilityProvider } from '@trimble/web3d-providers';
import {
  PickEventDetail,
  PickingTool,
  Web3DEventName,
  Web3DViewer,
} from '@technology/web3d';
import { SnapType } from '@trimble/web3d-providers';
import { Accessor, createEffect, createSignal, Setter } from 'solid-js';
import { PointCloudStylingType } from '@trimble/types';
import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import { PotreeManagerModel } from '@technology/web3d-plugin-potreemanager';
import { SharedCapabilityBase } from './base/shared-capability-base';
import { CapabilityCategory } from './base/models/capability-category';

export class PointCloudStylingCapability extends SharedCapabilityBase {
  public override category: CapabilityCategory = CapabilityCategory.Style;

  public readonly elevationPicked: Accessor<number>;
  private readonly setElevationPicked: Setter<number>;

  public readonly elevationPicking: Accessor<boolean>;
  private readonly setElevationPicking: Setter<boolean>;

  private readonly stylingType: Accessor<PointCloudStylingType>;

  private readonly elevationListeners = new Map<Web3DViewer, EventListener>();
  private readonly clearanceListeners = new Map<Web3DViewer, () => void>();
  private readonly savedSnapTypes = new Map<Web3DViewer, number[]>();
  private readonly activeToolListeners = new Map<Web3DViewer, EventListener>();

  constructor(pointCloudStylingProvider: PointCloudStylingCapabilityProvider) {
    super(pointCloudStylingProvider);

    const [elevationPicked, setElevationPicked] = createSignal<number>(0);
    this.elevationPicked = elevationPicked;
    this.setElevationPicked = setElevationPicked;

    const [elevationPicking, setElevationPicking] =
      createSignal<boolean>(false);
    this.elevationPicking = elevationPicking;
    this.setElevationPicking = setElevationPicking;

    const [stylingType, setStylingType] = createSignal<PointCloudStylingType>(
      PointCloudStylingType.Rgb
    );

    this.stylingType = stylingType;

    createEffect(() => {
      const styling = pointCloudStylingProvider.pointCloudStyling();
      setStylingType(styling.stylingType ?? PointCloudStylingType.Rgb);
    });

    createEffect(() => {
      const active = this.isActive();
      const stylingType = this.stylingType();
      const viewersWrappers = this.viewerWrappers();
      const elevationPicking = this.elevationPicking();

      for (const viewerWrapper of viewersWrappers) {
        if (
          stylingType === PointCloudStylingType.Elevation &&
          elevationPicking &&
          active
        ) {
          this.enablePickingTool(viewerWrapper.viewer);
          this.setSnapTypes(viewerWrapper.viewer);
          this.addElevationListener(viewerWrapper.viewer);
          this.addActiveToolListener(viewerWrapper.viewer);
          this.removeClearanceListener(viewerWrapper.viewer);
        } else if (stylingType === PointCloudStylingType.Clearance && active) {
          this.removeElevationListener(viewerWrapper.viewer);
          this.removeActiveToolListener(viewerWrapper.viewer);
          this.disablePickingTool(viewerWrapper.viewer);
          this.addClearanceListener(viewerWrapper.viewer);
          void this.setupClearance(viewerWrapper.viewer);
          this.resetSnapTypes(viewerWrapper.viewer);
        } else {
          this.removeElevationListener(viewerWrapper.viewer);
          this.removeActiveToolListener(viewerWrapper.viewer);
          this.removeClearanceListener(viewerWrapper.viewer);
          this.disablePickingTool(viewerWrapper.viewer);
          this.resetSnapTypes(viewerWrapper.viewer);
        }
      }
    });
  }

  public override cleanup(): Promise<void> {
    this.clearViewerState();

    return Promise.resolve();
  }

  public toggleElevationPicking(elevationPicking: boolean): void {
    this.setElevationPicking(elevationPicking);
  }

  private clearViewerState(): void {
    for (const viewerWrapper of this.viewerWrappers()) {
      this.removeElevationListener(viewerWrapper.viewer);
      this.removeActiveToolListener(viewerWrapper.viewer);
      this.removeClearanceListener(viewerWrapper.viewer);
      this.disablePickingTool(viewerWrapper.viewer);
      this.resetSnapTypes(viewerWrapper.viewer);
    }
  }

  private enablePickingTool(viewer: Web3DViewer): void {
    const pickingTool = viewer.tools.get(PickingTool);
    if (!pickingTool) return;
    pickingTool.enabled = true;
  }

  private disablePickingTool(viewer: Web3DViewer): void {
    const pickingTool = viewer.tools.get(PickingTool);
    if (!pickingTool) return;
    pickingTool.enabled = false;
  }

  private resetSnapTypes(viewer: Web3DViewer): void {
    const saved = this.savedSnapTypes.get(viewer);
    if (saved === undefined) return;
    viewer.settings.snapTypes = saved;
    this.savedSnapTypes.delete(viewer);
  }

  private setSnapTypes(viewer: Web3DViewer): void {
    if (!this.savedSnapTypes.has(viewer)) {
      this.savedSnapTypes.set(viewer, [...viewer.settings.snapTypes]);
    }
    viewer.settings.snapTypes = [
      SnapType.POINT as number,
      SnapType.CENTER_POINT as number,
      SnapType.FACE as number,
      SnapType.LINE as number,
    ];
  }

  private addElevationListener(viewer: Web3DViewer): void {
    // If already registered, don't register again.  This scenario is when viewers change.
    const elevationListener = this.elevationListeners.get(viewer);
    if (elevationListener) return;

    const handlePick = (e: Event): void => {
      const event = e as CustomEvent<PickEventDetail>;
      const { worldPosition } = event.detail;
      if (!worldPosition) return;
      const { z } = worldPosition;
      this.setElevationPicked(z);
    };

    viewer.addEventListener('pick' as Web3DEventName.Pick, handlePick);

    this.elevationListeners.set(viewer, handlePick);
  }

  private removeElevationListener(viewer: Web3DViewer): void {
    const elevationListener = this.elevationListeners.get(viewer);

    if (elevationListener) {
      viewer.removeEventListener('pick', elevationListener);
    }

    this.elevationListeners.delete(viewer);
  }

  private addActiveToolListener(viewer: Web3DViewer): void {
    const existingListener = this.activeToolListeners.get(viewer);
    if (existingListener) return;

    const handleActiveToolChanged = (): void => {
      this.setElevationPicking(false);
    };

    viewer.addEventListener('active-tool-changed', handleActiveToolChanged);
    this.activeToolListeners.set(viewer, handleActiveToolChanged);
  }

  private removeActiveToolListener(viewer: Web3DViewer): void {
    const listener = this.activeToolListeners.get(viewer);

    if (listener) {
      viewer.removeEventListener('active-tool-changed', listener);
    }

    this.activeToolListeners.delete(viewer);
  }

  private addClearanceListener(viewer: Web3DViewer): void {
    const clearanceListener = this.clearanceListeners.get(viewer);
    if (clearanceListener) return;

    const handleSelection = (): void => {
      void this.setupClearance(viewer);
    };

    viewer.selection.subscribe(handleSelection);
    this.clearanceListeners.set(viewer, handleSelection);
  }

  private removeClearanceListener(viewer: Web3DViewer): void {
    const clearanceListener = this.clearanceListeners.get(viewer);

    if (clearanceListener) {
      viewer.selection.unsubscribe(clearanceListener);
    }

    this.clearanceListeners.delete(viewer);
  }

  private async setupClearance(viewer: Web3DViewer): Promise<void> {
    const stylingType = this.stylingType();
    if (stylingType !== PointCloudStylingType.Clearance) return;

    const pointClouds = viewer.getModels(PotreeManagerModel);

    for (const pointCloud of pointClouds) {
      pointCloud.removeAllClearanceData();
    }

    if (pointClouds.length === 0) return;

    let trimbimId: string | undefined;
    let trimbim: TrimbimModel | undefined;
    const selectedIds: number[] = [];
    for (const selection of viewer.selection.entries()) {
      const modelId = selection[0];
      const model = viewer.getModel(modelId);

      if (model instanceof TrimbimModel) {
        const entityIds = selection[1];
        selectedIds.push(...entityIds);
        trimbimId = modelId;
        trimbim = model;
        break;
      }
    }

    if (selectedIds.length === 0 || !trimbimId || !trimbim) return;

    for (const pointCloud of pointClouds) {
      pointCloud.updateClearanceModelSelection(trimbimId, selectedIds, true);
    }
  }
}
